import React from 'react';
import active from "../../../../src/assests/img/active-icon.svg";
import { useConfigurationContext } from './Context/ConfigurationContext';
import SkeletonElement from 'Components/GMS/Category/Shimmer/SkeletonElement';
import { GMS_API_OPTIONS, IMPORT_TYPE_OPTIONS } from './configurationConstants';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { getFieldError } from './validationUtils';

type NetworkSetupTabProps = {
    id?: string;
};

export const NetworkSetupTab = ({ id = 'network-setup-tab' }: NetworkSetupTabProps) => {
    const { currentConfig, isEditMode, isLoading, updateNetworkSetup, updateGmsEndpoint, validationErrors } = useConfigurationContext();

    const handleNetworkChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const updatedValue = type === 'checkbox'
            ? (e.target as HTMLInputElement).checked
            : type === 'number'
                ? Number(value)
                : value;

        updateNetworkSetup({ [name]: updatedValue });
    };

    const handleImportTypeChange = (value: string | number) => {
        updateNetworkSetup({ import_type: String(value) });
    };

    const handleGmsEndpointChange = (e: string | number) => {
        updateGmsEndpoint(e.toString() || null);
    };

    const networkSetup = currentConfig?.network_setup;
    const gmsEndpoint = currentConfig?.gms_endpoint;
    const hasExistingConnection = networkSetup?.sftp_host !== null && networkSetup?.sftp_host !== '';

    const getInputStyle = (fieldKey: string) => {
        const hasError = getFieldError(validationErrors, fieldKey);
        const baseStyle = !isEditMode
            ? 'tw-cursor-not-allowed tw-border tw-border-solid tw-bg-grey-100 tw-text-grey-500 tw-rounded tw-w-full tw-min-w-[335px] tw-h-9 tw-pl-2.5'
            : "tw-bg-white tw-border tw-border-solid tw-rounded tw-w-full tw-min-w-[335px] tw-h-9 tw-pl-2.5";
        const errorStyle = hasError ? 'tw-border-red-500' : '';
        return `${baseStyle} ${errorStyle}`;
    };

    if (isLoading) {
        return (
            <div data-testid={`${id}-loading`}>
                <div className="form-group" data-testid={`${id}-skeleton-group-0`}>
                    <div className="tw-flex tw-justify-between tw-me-4">
                        <SkeletonElement type="text50" />
                    </div>
                    <SkeletonElement type="title" />
                </div>
                <div className="form-group" data-testid={`${id}-skeleton-group-1`}>
                    <SkeletonElement type="text50" />
                    <SkeletonElement type="title" />
                </div>
                <div className="form-group" data-testid={`${id}-skeleton-group-2`}>
                    <SkeletonElement type="text50" />
                    <SkeletonElement type="title" />
                </div>
                <div className="form-group" data-testid={`${id}-skeleton-group-3`}>
                    <SkeletonElement type="text50" />
                    <SkeletonElement type="title" />
                </div>
                <div className="form-group" data-testid={`${id}-skeleton-group-4`}>
                    <SkeletonElement type="text50" />
                    <SkeletonElement type="title" />
                </div>
                <div className="form-group" data-testid={`${id}-skeleton-group-5`}>
                    <SkeletonElement type="text50" />
                </div>
            </div>
        );
    }

    if (!networkSetup) return null;

    return (
        <div data-testid={id}>
            <form onSubmit={(e) => e.preventDefault()} data-testid={`${id}-form`}>
                <div className="form-group" data-testid={`${id}-import-type-group`}>
                    <label htmlFor="import_type">Import Type</label>
                    <Dropdown
                        value={networkSetup?.import_type ?? ''}
                        onChange={handleImportTypeChange}
                        placeholder="Select Import Type"
                        label=""
                        options={IMPORT_TYPE_OPTIONS}
                        disabled={!isEditMode}
                    />
                </div>
                <div className="form-group" data-testid={`${id}-sftp-host-group`}>
                    <div className="tw-flex tw-justify-between tw-me-4">
                        <label className="form-caption" htmlFor="sftp_host">
                            SFTP URL
                        </label>
                        {hasExistingConnection && !isEditMode && (
                            <img
                                src={active}
                                alt="Active"
                                data-testid={`${id}-active-icon`}
                            />
                        )}
                    </div>
                    <input
                        className={getInputStyle('sftp_host')}
                        type="text"
                        disabled={!isEditMode}
                        id="sftp_host"
                        name="sftp_host"
                        placeholder="Enter url"
                        value={networkSetup.sftp_host || ''}
                        onChange={handleNetworkChange}
                        data-testid={`${id}-sftp-host-input`}
                    />
                </div>

                <div className="form-group" data-testid={`${id}-username-group`}>
                    <label className="form-caption" htmlFor="sftp_username">
                        Username
                    </label>
                    <input
                        className={getInputStyle('sftp_username')}
                        type="text"
                        disabled={!isEditMode}
                        id="sftp_username"
                        placeholder="Enter username"
                        name="sftp_username"
                        value={networkSetup.sftp_username || ''}
                        onChange={handleNetworkChange}
                        data-testid={`${id}-username-input`}
                    />
                </div>

                <div className="form-group" data-testid={`${id}-password-group`}>
                    <label htmlFor="sftp_password">Password</label>
                    <input
                        className={getInputStyle('sftp_password')}
                        type="password"
                        id="sftp_password"
                        disabled={!isEditMode}
                        placeholder="Enter password"
                        name="sftp_password"
                        value={networkSetup.sftp_password || ''}
                        onChange={handleNetworkChange}
                        data-testid={`${id}-password-input`}
                    />
                </div>

                <div className="form-group" data-testid={`${id}-port-group`}>
                    <label htmlFor="port">Port</label>
                    <input
                        className={getInputStyle('port')}
                        type="number"
                        id="port"
                        disabled={!isEditMode}
                        placeholder="Enter Port"
                        name="port"
                        value={networkSetup.port || ''}
                        onChange={handleNetworkChange}
                        data-testid={`${id}-port-input`}
                    />
                </div>

                <div className="form-group" data-testid={`${id}-file-location-group`}>
                    <label htmlFor="file_location">File location</label>
                    <input
                        className={getInputStyle('file_location')}
                        type="text"
                        id="file_location"
                        disabled={!isEditMode}
                        placeholder="Enter File Location"
                        name="file_location"
                        value={networkSetup.file_location || ''}
                        onChange={handleNetworkChange}
                        data-testid={`${id}-file-location-input`}
                    />
                </div>

                <div className="form-group" data-testid={`${id}-gms-endpoint-group`}>
                    <label htmlFor="gms_endpoint">GMS Api Endpoint</label>
                    <Dropdown
                        value={gmsEndpoint || ''}
                        onChange={handleGmsEndpointChange}
                        placeholder="Select GMS Endpoint"
                        label=""
                        options={GMS_API_OPTIONS}
                        disabled={!isEditMode}
                        usePortal
                    />
                </div>

                <div className="form-group" data-testid={`${id}-detailed-logging-group`}>
                    <label htmlFor="is_debugging">Detailed Logging</label>
                    <ToggleButton
                        value={networkSetup.is_debugging}
                        disabled={!isEditMode}
                        name="is_debugging"
                        onChange={handleNetworkChange}
                        data-testid={`${id}-detailed-logging-toggle`}
                    />
                </div>
            </form>
        </div>
    );
};